// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { Popover, Button, Indicator, Stack, Title, Switch } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { strongValues } from 'helpers/strongEntries';

export type SpoolingSettingsOption<T> = { setting: T; label: string };

type Props<T extends string> = {
  settingsState: Record<T, boolean>;
  settings: SpoolingSettingsOption<T>[];
  toggleSetting: (setting: T) => void;
};

export const IsolateBehaviorPopover = <T extends string>({ settingsState, settings, toggleSetting }: Props<T>) => {
  const [opened, setOpened] = useState(false);
  const active = strongValues(settingsState).some((v) => v);
  return (
    <Popover opened={opened} onChange={setOpened}>
      <Popover.Target>
        <Indicator disabled={!active}>
          <AgGridStyleTooltip label="Model isolation behavior" withArrow openDelay={200} hidden={opened}>
            <Button size="xs" variant="light" onClick={() => setOpened((o) => !o)} p={2}>
              <EvolveIcon icon="Isolate" />
            </Button>
          </AgGridStyleTooltip>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack pb="xs">
          <Title order={5}>Model isolation behavior</Title>
          {settings.map(({ setting, label }) => (
            <Switch
              key={setting}
              label={label}
              checked={settingsState[setting]}
              onChange={() => toggleSetting(setting)}
              styles={{ label: { fontSize: 14 } }}
              size="xs"
            />
          ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
